import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import ProviderIcon from './ProviderIcon';

/**
 * "+ Other" (owner, 06.10.2026): a click lists the providers to add an account
 * for, each with its icon — GitLab, Gitea (also Forgejo / Codeberg), and
 * GitHub by personal access token (07.10.2026), last, and only while no GitHub
 * token is connected. No tooltip: the label says it.
 *
 * @param {Array} providers  registry entries that sign in with a token
 * @param {boolean} githubToken  a GitHub token is already connected
 * @param {(provider) => void} onPick
 */
export default function GitAddAccountMenu({ providers, githubToken, onPick }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Closes like any menu: Escape or a click elsewhere.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !e.target.closest?.('.git-add-wrap')) setOpen(false);
    };
    document.addEventListener('keydown', close);
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  const items = [
    ...providers.filter((p) => p.id !== 'github'),
    ...(githubToken ? [] : providers.filter((p) => p.id === 'github')),
  ];
  if (items.length === 0) return null;

  return (
    <span className="git-acct-seg git-add-wrap">
      <button
        type="button"
        className="git-acct-btn git-acct-btn--accent"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('gitAccounts.addAnAccount')}
        onClick={() => setOpen((v) => !v)}
      >
        <Plus size={13} />
        {t('gitAccounts.other')}
      </button>
      {open && (
        <div className="git-add-menu" role="menu" aria-label={t('gitAccounts.addAnAccount')}>
          {items.map((p) => {
            const isGitHub = p.id === 'github';
            return (
              <button
                key={p.id}
                type="button"
                role="menuitem"
                aria-label={isGitHub ? t('gitAccounts.githubTokenAria') : t('gitAccounts.menuItem', { provider: p.label })}
                onClick={() => {
                  setOpen(false);
                  onPick(p);
                }}
              >
                <ProviderIcon id={p.id} size={14} /> {isGitHub ? t('gitAccounts.githubTokenItem') : p.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
